import React, { useState, useEffect } from "react";
import { Container, Button, Form, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaTrashAlt, FaPaperPlane } from "react-icons/fa";
import "../../Styles/yellocalfcss/PendingInvoices.css";
import ApiConfig from "../../api/ApiConfig";
import { simplePostCall } from "../../api/ApiServices";
import ScaleLoader from "react-spinners/ScaleLoader";

const PendingInvoices = () => {
  const navigate = useNavigate();

  // --- States ---
  const [invoices, setInvoices] = useState([]);
  const [studentList, setStudentList] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isBulkSending, setIsBulkSending] = useState(false); // New state for bulk send button
  const [selectedInvoices, setSelectedInvoices] = useState([]); // State for checkboxes

  const [filters, setFilters] = useState({
    invoiceId: "",
    student: "",
    year: "",
    status: "pending",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const invoicesPerPage = 15;

  // --- 1. Fetch Data on Mount ---
  useEffect(() => {
    loadBillDetails();
  }, []);

  const loadBillDetails = async () => {
    setIsLoading(true);
    try {
      const requestBody = {
        customer_id: Number(localStorage.getItem("customer_id")) || 1005,
        api_key:
          localStorage.getItem("api_key") || "b9359f494e77b6589ce12dd62518c585",
        user_id: Number(localStorage.getItem("user_id")) || 14857,
        language: "en",
        invoice_search: filters.invoiceId,
        student_search: filters.student,
        year_search: filters.year,
        status_search: filters.status,
        date_search: "",
        due_date_search: "",
      };

      const response = await simplePostCall(
        ApiConfig.pendingInvoice,
        JSON.stringify(requestBody),
      );

      if (response?.result) {
        setInvoices(response.data || []);
        setStudentList(response.student || []);
        setAcademicYears(response.academic_year || []);
        setSelectedInvoices([]); // Reset selection on new search
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // --- 2. Selection Logic ---
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = currentInvoices.map((inv) => inv.fee_invoice_id);
      setSelectedInvoices(allIds);
    } else {
      setSelectedInvoices([]);
    }
  };

  const handleCheckboxChange = (id) => {
    setSelectedInvoices((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  // --- 3. Bulk Send Logic ---
  const handleBulkSend = async () => {
    if (selectedInvoices.length === 0) {
      alert("Please select at least one invoice to send.");
      return;
    }

    setIsBulkSending(true);
    try {
      const requestBody = {
        customer_id: Number(localStorage.getItem("customer_id")),
        api_key: localStorage.getItem("api_key"),
        language: "en",
        user_id: Number(localStorage.getItem("user_id")),
        ch_list: selectedInvoices, // Dynamically pass the selected IDs
      };

      const response = await simplePostCall(
        ApiConfig.smsbulksend,
        JSON.stringify(requestBody),
      );

      if (response?.result) {
        alert("Bulk SMS sent successfully!");
        setSelectedInvoices([]); // Clear selection after success
      } else {
        alert(response?.message || "Failed to send Bulk SMS");
      }
    } catch (error) {
      console.error("Bulk Send Error:", error);
      alert("An error occurred while sending.");
    } finally {
      setIsBulkSending(false);
    }
  };

  const handleClear = () => {
    setFilters({ invoiceId: "", student: "", year: "", status: "pending" });
    loadBillDetails();
  };

  // Pagination Logic
  const indexOfLastInvoice = currentPage * invoicesPerPage;
  const indexOfFirstInvoice = indexOfLastInvoice - invoicesPerPage;
  const currentInvoices = invoices.slice(
    indexOfFirstInvoice,
    indexOfLastInvoice,
  );

  return (
    <div className="pending-invoices-main-container my-4">
      <Container fluid className="pending-invoices-fluid-wrapper">
        <div className="pending-invoices-header-area">
          <h1 className="pending-invoices-title-heading">
            Pending Fee Invoices
          </h1>
          <div className="pending-invoices-button-area">
            <Button
              className="pending-invoices-collect-button"
              onClick={() => navigate("/collectpayment")}
            >
              Collect Fee
            </Button>
            <Button
              className="pending-invoices-bulk-button"
              onClick={handleBulkSend}
              disabled={isBulkSending || selectedInvoices.length === 0}
            >
              {isBulkSending ? (
                <Spinner size="sm" />
              ) : (
                `Bulk Send (${selectedInvoices.length})`
              )}
            </Button>
          </div>
        </div>

        {/* --- Filter Section --- */}
        <div className="pending-invoices-filter-area">
          <div
            className="pending-invoices-filter-inputs-wrapper"
            style={{ marginTop: "22px" }}
          >
            <div style={{ position: "relative" }}>
              <FaSearch
                className="filter-icon"
                style={{
                  position: "absolute",
                  left: "10px",
                  top: "12px",
                  color: "#888",
                }}
              />
              <Form.Control
                type="text"
                placeholder="Invoice ID"
                value={filters.invoiceId}
                onChange={(e) =>
                  setFilters({ ...filters, invoiceId: e.target.value })
                }
                style={{ paddingLeft: "34px" }}
              />
            </div>

            <Form.Select
              value={filters.student}
              onChange={(e) =>
                setFilters({ ...filters, student: e.target.value })
              }
            >
              <option value="">All Students</option>
              {studentList.map((s) => (
                <option key={s.student_id} value={s.student_id}>
                  {s.student_name}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              value={filters.year}
              onChange={(e) => setFilters({ ...filters, year: e.target.value })}
            >
              <option value="">All Academic Years</option>
              {academicYears.map((y) => (
                <option key={y.academic_year_id} value={y.academic_year_id}>
                  {y.academic_year_name}
                </option>
              ))}
            </Form.Select>

            <Button className="btn custom-btn" onClick={loadBillDetails}>
              Search
            </Button>
            <Button className="btn custom-btn" onClick={handleClear}>
              Clear
            </Button>
          </div>
        </div>

        {/* --- Table Header --- */}
        <div className="pending-invoices-table-header-card mt-4">
          <div className="pending-invoices-grid-structure" style={{ gridTemplateColumns: '40px 60px 1fr 1.5fr 1fr 1fr 1fr 1fr 1fr 1fr 120px' }}>
            
            <div className="pending-invoices-header-column">
              <Form.Check
                type="checkbox"
                onChange={handleSelectAll}
                checked={
                  selectedInvoices.length === currentInvoices.length &&
                  currentInvoices.length > 0
                }
              />
            </div>
            <div className="pending-invoices-header-column">Sr.No</div>
            {/* <div className="pending-invoices-header-column">Invoice ID</div> */}
            <div className="pending-invoices-header-column">Student</div>
            <div className="pending-invoices-header-column">Academic Year</div>
            <div className="pending-invoices-header-column">Demand Date</div>
            <div className="pending-invoices-header-column">Due Date</div>
            <div className="pending-invoices-header-column">Total</div>
            <div className="pending-invoices-header-column">Paid</div>
            <div className="pending-invoices-header-column">Balance</div>
            <div className="pending-invoices-header-column">Action</div>
          </div>
        </div>

        {/* --- Data List --- */}
        <div className="pending-invoices-data-cards-container">
          {isLoading ? (
            <div className="text-center p-3">
              <ScaleLoader
                color="#F4BB44"
                style={{ textAlign: "center", padding: "10px" }}
              />
            </div>
          ) : currentInvoices.length > 0 ? (
            currentInvoices.map((invoice, index) => (
              <div
                key={invoice.fee_invoice_id}
                className="pending-invoices-individual-card"
              >
                <div
                  className="pending-invoices-grid-structure"
                  style={{
                    gridTemplateColumns:
                      "40px 60px 1fr 1.5fr 1fr 1fr 1fr 1fr 1fr 1fr 120px",
                  }}
                >
                  <div className="pending-invoices-data-column">
                    <Form.Check
                      type="checkbox"
                      checked={selectedInvoices.includes(
                        invoice.fee_invoice_id,
                      )}
                      onChange={() =>
                        handleCheckboxChange(invoice.fee_invoice_id)
                      }
                    />
                  </div>
                  <div className="pending-invoices-data-column">
                    {indexOfFirstInvoice + index + 1}
                  </div>
                  <div className="pending-invoices-data-column">
                    {invoice.fee_invoice_id}
                  </div>
                  <div className="pending-invoices-data-column">
                    {invoice.academic_year_name}
                  </div>
                  <div className="pending-invoices-data-column">
                    {academicYears.find(
                      (y) =>
                        y.academic_year_id ===
                        invoice.fee_invoice_accademic_year,
                    )?.academic_year_name || invoice.fee_invoice_accademic_year}
                  </div>
                  <div className="pending-invoices-data-column">
                    {invoice.fee_invoice_demand_date}
                  </div>
                  <div className="pending-invoices-data-column">
                    {invoice.fee_invoice_due_date}
                  </div>
                  {/* <div className="pending-invoices-data-column">
                    {invoice.fee_invoice_amount_total}
                  </div> */}
                  <div className="pending-invoices-data-column">
                    {invoice.fee_invoice_total_amount_paid}
                  </div>
                  <div className="pending-invoices-data-column text-danger fw-bold">
                    {invoice.balance}
                  </div>

                  <div className="pending-invoices-data-column d-flex gap-2 justify-content-center">
                    <button
                      className="btn-sm border-0 bg-transparent"
                      title="View"
                      onClick={() =>
                        navigate("/Pendingviewinvoice", {
                          state: { id: invoice.fee_invoice_id },
                        })
                      }
                    >
                      <FaSearch color="#666" />
                    </button>
                    <button
                      className="btn-sm border-0 bg-transparent"
                      title="Delete"
                    >
                      <FaTrashAlt color="red" />
                    </button>
                    <button
                      className="btn-sm border-0 bg-transparent"
                      title="Send"
                      onClick={() => {
                        setSelectedInvoices([invoice.fee_invoice_id]); // Select just this one
                        handleBulkSend(); // Call send
                      }}
                    >
                      <FaPaperPlane color="#ffa500" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center p-4">No pending invoices found.</div>
          )}
        </div>

        <div className="pending-invoices-pagination-text mt-3">
          Showing {currentInvoices.length} of {invoices.length} items
        </div>
      </Container>
    </div>
  );
};

export default PendingInvoices;
